import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    FileText,
    Plus,
    Trash2,
    Pencil,
    FileCheck2,
    Info,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';
import UtdelningsGuide from '../components/UtdelningsGuide';

/** Board roles per the backend contract ('ledamot'|'ordforande'|'vd'|'suppleant'). */
const BOARD_ROLES = [
    { value: 'ledamot', label: 'Styrelseledamot' },
    { value: 'ordforande', label: 'Styrelseordförande' },
    { value: 'vd', label: 'VD' },
    { value: 'suppleant', label: 'Suppleant' },
];
const ROLE_LABEL = Object.fromEntries(BOARD_ROLES.map((r) => [r.value, r.label]));
const REPORT_STATUS_LABELS = {
    draft: 'Utkast',
    ready_for_signing: 'Klar för underskrift',
    signed: 'Underskriven',
    stamma_held: 'Stämma hållen',
    completed: 'Klar',
};

/** Parse a Swedish-typed amount ("12 500,50") into a number. */
function parseAmount(raw) {
    if (raw == null) return NaN;
    const cleaned = String(raw).replace(/[\s ]/g, '').replace(',', '.');
    if (cleaned === '') return NaN;
    return Number(cleaned);
}

/** Empty input = 0. */
function parseOptionalAmount(raw) {
    if (String(raw ?? '').trim() === '') return 0;
    return parseAmount(raw);
}

function round2(n) { return Math.round(n * 100) / 100; }
function isoDate(v) { return v ? String(v).slice(0, 10) : ''; }

/**
 * Belopp per aktie i hela ören (ABL 18:3) — exakt när delningen går jämnt
 * ut, annars "ca" (jämn-delning valideras separat innan förslaget sparas).
 */
function perAktieStr(total, antal) {
    const ore = Math.round(total * 100);
    if (ore % antal === 0) {
        const per = ore / antal;
        return per % 100 === 0
            ? (per / 100).toLocaleString('sv-SE')
            : (per / 100).toFixed(2).replace('.', ',');
    }
    return `ca ${(total / antal).toFixed(2).replace('.', ',')}`;
}

/** Report row + suggestions → the editable form snapshot. */
function formFromReport(report, suggestions) {
    const fb = report?.fb_sections || {};
    const notes = report?.notes || {};
    // Summan till förfogande läses ALLTID ur den färska basen, aldrig ur det
    // sparade förslaget. Utdelningen är användarens beslut och ska överleva;
    // summan är fritt eget kapital enligt bokföringen och ändras när
    // bokföringen ändras — t.ex. av reparationsverktyget. Tvärtom (sparat
    // först) gav kanariet en summa på 295 795,94 bredvid poster som räknats
    // om till 46 021,48 + 58 555,23.
    const till = suggestions?.resultatdisposition?.tillDisposition
        ?? report?.resultatdisposition?.tillDisposition ?? 0;
    const utdelning = report?.resultatdisposition?.utdelning ?? 0;
    // null-förslag (beräkningen otillgänglig) blir TOMT fält som pulserar
    // för inmatning — aldrig ett auktoritativt utseende "0".
    const medelantal = notes.medelantal_anstallda?.count
        ?? suggestions?.medelantalAnstallda?.count ?? '';
    return {
        // Sätet (registerFAKTUM från Bolagsverket via TIC) förifylls tyst när
        // fältet är tomt — alltid redigerbart; utan berikning förblir fältet
        // tomt och pulserar som idag. Verksamhetstexten förifylls däremot
        // ALDRIG tyst: den är prosa och accepteras via förslags-chip i
        // dokumentvyn, så bolagsordningstext inte hamnar i ÅR av misstag.
        verksamhet: fb.verksamhet || '',
        sate: fb.sate || suggestions?.sate?.value || '',
        vasentligaHandelser: fb.vasentliga_handelser || '',
        flerarsKommentar: fb.flerarsoversikt_kommentar || '',
        yttrande18_4: fb.yttrande_18_4 || '',
        avskrivningsprinciper: notes.avskrivningsprinciper?.text || '',
        utdelning: utdelning ? String(utdelning) : '',
        // ABL 18:3: förslaget ska ange belopp per aktie — antalet aktier
        // ger per-aktie-uppgiften i ÅR, protokoll och PDF.
        antalAktier: report?.resultatdisposition?.antalAktier
            ? String(report.resultatdisposition.antalAktier) : '',
        // ABL 18:3 andra-tredje styckena: guiden stödjer bara kontant
        // utdelning, ett aktieslag och icke-avstämningsbolag — användaren
        // intygar förutsättningarna (finalize-grind på servern).
        utdForutsattningar: Boolean(report?.resultatdisposition?.forutsattningar?.enAktieslag
            && report?.resultatdisposition?.forutsattningar?.kontantUtdelning
            && report?.resultatdisposition?.forutsattningar?.ejAvstamningsbolag),
        boardMembers: Array.isArray(report?.board_members) && report.board_members.length > 0
            ? report.board_members.map((m) => ({ name: m.name || '', role: m.role || 'ledamot' }))
            : [{ name: '', role: 'ledamot' }],
        // ÅRL 2:7 — intyg att listan omfattar SAMTLIGA registrerade
        // ledamöter + ev. VD (finalize-grind; registeravstämning mot
        // Bolagsverket finns inte utan API).
        styrelseKomplettAttest: fb.styrelse_komplett_attest === true,
        medelantalAnstallda: String(medelantal),
        stalldaSakerheter: notes.stallda_sakerheter?.amount != null
            ? String(notes.stallda_sakerheter.amount) : '',
        // ÅRL 5:14 — säkerheternas ART och FORM, obligatorisk när belopp anges.
        stalldaSakerheterText: notes.stallda_sakerheter?.text || '',
        // Obligatoriska ja/nej (Codex-granskning 6): finns-flaggan skiljer
        // "har inga" från "aldrig besvarad"; legacy-data (belopp/text utan
        // flagga) tolkas som ja så befintliga svar aldrig tystas.
        stalldaSakerheterFinns: typeof notes.stallda_sakerheter?.finns === 'boolean'
            ? notes.stallda_sakerheter.finns
            : ((Number(notes.stallda_sakerheter?.amount) > 0
                || String(notes.stallda_sakerheter?.text || '').trim() !== '') ? true : null),
        eventualforpliktelser: notes.eventualforpliktelser?.amount != null
            ? String(notes.eventualforpliktelser.amount) : '',
        eventualforpliktelserText: notes.eventualforpliktelser?.text || '',
        eventualforpliktelserFinns: typeof notes.eventualforpliktelser?.finns === 'boolean'
            ? notes.eventualforpliktelser.finns
            : ((Number(notes.eventualforpliktelser?.amount) > 0
                || String(notes.eventualforpliktelser?.text || '').trim() !== '') ? true : null),
        // ÅRL 5:13 — del av långfristiga skulder med förfall > 5 år; 0 är ett
        // giltigt och meningsfullt svar, tomt fält = obesvarat.
        langfristigaSkulder5ar: notes.langfristiga_skulder_5ar?.amount != null
            ? String(notes.langfristiga_skulder_5ar.amount) : '',
        // ÅRL 5:18 — obligatorisk ja/nej-fråga; null = obesvarad (pulserar).
        // ÅRL 5:8 — överföringar mellan AT-poster: obligatorisk ja/nej när
        // anläggningsnoter finns (kan inte läsas ur huvudboken).
        atOverforingarFinns: typeof notes.at_overforingar?.finns === 'boolean'
            ? notes.at_overforingar.finns : null,
        atOverforingarText: notes.at_overforingar?.text || '',
        // Rättelse av fel (nya K2, BFNAR 2025:2 p. 2.12/18.7A) — frågan
        // visas bara när servern flaggar asksRattelseFel (FY2026+).
        rattelseFelFinns: typeof notes.rattelse_fel?.finns === 'boolean'
            ? notes.rattelse_fel.finns : null,
        rattelseFelText: notes.rattelse_fel?.text || '',
        lanLedandeFinns: typeof notes.lan_ledande?.finns === 'boolean'
            ? notes.lan_ledande.finns : null,
        lanLedandeText: notes.lan_ledande?.text || '',
        // ÅRL 5:21 — obligatorisk ja/nej-fråga; fälten krävs vid ja.
        moderAr: typeof notes.moderforetag?.arDotterforetag === 'boolean'
            ? notes.moderforetag.arDotterforetag : null,
        moderNamn: notes.moderforetag?.namn || '',
        moderOrgnr: notes.moderforetag?.orgnr || '',
        moderSate: notes.moderforetag?.sate || '',
        goodwillText: notes.goodwill?.text || '', // ÅRL 5:9
        uppskrivningsfondText: notes.uppskrivningsfond?.text || '', // ÅRL 5:11
        customNotes: Array.isArray(notes.custom)
            ? notes.custom.map((c) => ({ title: c?.title || '', text: c?.text || '' }))
            : [],
        // Revisorsfrågan (ABL 7:11, ÅRL 8:3) — obligatorisk före färdigställande.
        harRevisor: typeof fb.revisor?.harRevisor === 'boolean'
            ? fb.revisor.harRevisor : null,
        revisorNamn: fb.revisor?.namn || '',
        revisorByra: fb.revisor?.byra || '',
        // ABL 25:13 — intyg att styrelsen hanterar kontrollbalansfrågan när
        // indikatorn slår (servern kräver det vid färdigställande).
        kontrollbalansAttest: fb.kontrollbalans_attest === true,
        handelserEfterAret: notes.handelser_efter_aret?.text || '',
        handelserEfterAretFinns: typeof notes.handelser_efter_aret?.finns === 'boolean'
            ? notes.handelser_efter_aret.finns
            : (String(notes.handelser_efter_aret?.text || '').trim() !== '' ? true : null),
        andradePrinciper: notes.andrade_principer?.text || '',
        exceptionellaPoster: notes.exceptionella_poster?.text || '',
        exceptionellaPosterFinns: typeof notes.exceptionella_poster?.finns === 'boolean'
            ? notes.exceptionella_poster.finns
            : (String(notes.exceptionella_poster?.text || '').trim() !== '' ? true : null),
        posterFleraPoster: notes.poster_flera_poster?.text || '',
        ekonomiskaArrangemang: notes.ekonomiska_arrangemang?.text || '',
        _till: till,
    };
}

/**
 * Step 5 — Årsredovisning som DOKUMENTVY: sidan ritar samma modell som
 * PDF:en trycker (GET annual-report/render-model), i dokumentets ordning —
 * framsida, förvaltningsberättelse, resultat- och balansräkning, noter,
 * underskrifter. Härledda siffror är alltid läs-endast; de fält användaren
 * ska fylla i ligger INLINE i dokumentet och markeras med en pulserande
 * prick (samma bk-pulse som bokföringsguiden) tills de är ifyllda.
 * Redigeringslogiken är oförändrad: autosave på blur (debounced PUT),
 * "Färdigställ" kör serverns komplettgrind, och från signerat läge är allt
 * låst tills underskrifterna ångras i steg 6.
 */
/**
 * En redigerbar zon i dokumentet. `needsInput` tänder pulsen (samma
 * bk-pulse som bokföringsguidens stegindikator) — den betyder "här
 * behöver du skriva". I läs-läge renderas bara texten.
 *
 * Zone/ProseZone/StatementTable ligger på MODULNIVÅ med stegets state som
 * props: definierade inuti komponenten fick de ny identitet varje render,
 * React monterade om inputen per tangenttryck och fokus försvann efter en
 * bokstav.
 */
const Zone = ({ readOnly, needsInput, children }) => (
    <div className={`ard-zone${!readOnly && needsInput ? ' ard-zone-needs' : ''}`}>
        {!readOnly && needsInput && <span className="ard-dot" aria-hidden="true" />}
        {children}
    </div>
);

const ProseZone = ({
    form, readOnly, setField, scheduleSave,
    field, placeholder, required = false, ariaLabel,
}) => {
    const value = form[field];
    if (readOnly) {
        return value.trim() ? <p className="ard-p">{value}</p> : null;
    }
    return (
        <Zone readOnly={readOnly} needsInput={required && value.trim() === ''}>
            <textarea
                className="ard-textarea"
                rows={2}
                placeholder={placeholder}
                aria-label={ariaLabel || placeholder}
                value={value}
                onChange={setField(field)}
                onBlur={scheduleSave}
            />
        </Zone>
    );
};

/**
 * Obligatorisk ja/nej-fråga i dokumentet (ÅRL 5:18/5:21 samt revisorsfrågan)
 * — pulserar tills den är BESVARAD; nej är ett lika giltigt svar som ja.
 * Renderas bara i redigeringsläge: svaret trycks (i förekommande fall) via
 * notmodellen, inte som fråga.
 */
const JaNejZone = ({ readOnly, question, value, onAnswer, hint, children }) => {
    if (readOnly) return null;
    return (
        <Zone readOnly={readOnly} needsInput={value == null}>
            <div className="ard-janej">
                <span className="ard-janej-q">{question}</span>
                <div className="ard-janej-btns">
                    <button
                        type="button"
                        className={`btn btn-sm ${value === true ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => onAnswer(true)}
                    >
                        Ja
                    </button>
                    <button
                        type="button"
                        className={`btn btn-sm ${value === false ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => onAnswer(false)}
                    >
                        Nej
                    </button>
                </div>
            </div>
            {hint && <p className="ard-hint">{hint}</p>}
            {children}
        </Zone>
    );
};

const StatementTable = ({ children, year, prevYear, noteCol = false }) => (
    <div className="ard-table-wrap">
        <table className="ard-table">
            <thead>
                <tr>
                    <th aria-label="Post" />
                    {noteCol && <th className="ard-note-ref">Not</th>}
                    <th className="ard-num">{year}</th>
                    <th className="ard-num">{prevYear ?? 'Föreg. år'}</th>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    </div>
);

const AnnualReportStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [report, setReport] = useState(null);
    const [suggestions, setSuggestions] = useState(null);
    const [model, setModel] = useState(null); // render-model (dokumentskelettet)
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const [form, setForm] = useState(null);
    const [editing, setEditing] = useState(false); // "Ändra" at ready_for_signing
    const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
    const [saveError, setSaveError] = useState(null);
    const saveTimer = useRef(null);
    const formRef = useRef(null);
    formRef.current = form;

    const [finalizing, setFinalizing] = useState(false);
    const [finalizeProblems, setFinalizeProblems] = useState(null);
    const [finalizeError, setFinalizeError] = useState(null);
    const [openingPdf, setOpeningPdf] = useState(false);
    const [showAllNotes, setShowAllNotes] = useState(false);

    // ---- Load -------------------------------------------------------------

    const fetchReport = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const [reportResp, modelResp] = await Promise.all([
                YearEndService.getAnnualReport(companyId, processId),
                YearEndService.getAnnualReportRenderModel(companyId, processId),
            ]);
            const r = reportResp?.data?.report || null;
            const s = reportResp?.data?.suggestions || null;
            setReport(r);
            setSuggestions(s);
            setModel(modelResp?.data || null);
            setForm(formFromReport(r, s));
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte hämta årsredovisningen. Försök igen senare.');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchReport({ silent: true }); }, [fetchReport]);

    const status = report?.status || 'draft';
    const isDraft = status === 'draft';
    const isSignedOrLater = ['signed', 'stamma_held', 'completed'].includes(status);
    const readOnly = !isDraft && !(status === 'ready_for_signing' && editing);

    // ---- Derived disposition ----------------------------------------------

    const dispBasis = suggestions?.resultatdisposition || null;
    const till = form?._till ?? dispBasis?.tillDisposition ?? 0;
    const utdelningValue = parseOptionalAmount(form?.utdelning);
    const utdelningValid = Number.isFinite(utdelningValue) && utdelningValue >= 0;
    const balanseras = utdelningValid ? round2(till - utdelningValue) : null;
    const dispositionOk = utdelningValid && balanseras != null && balanseras >= -0.005;

    // ---- Autosave (debounced PUT on blur) ---------------------------------

    const buildPatch = useCallback(() => {
        const f = formRef.current;
        if (!f) return null;
        const patch = {
            fb_sections: {
                verksamhet: f.verksamhet.trim(),
                sate: f.sate.trim(),
                vasentliga_handelser: f.vasentligaHandelser.trim(),
                flerarsoversikt_kommentar: f.flerarsKommentar.trim(),
                yttrande_18_4: f.yttrande18_4.trim(),
                // Revisorsfrågan (ABL 7:11, ÅRL 8:3): obligatorisk ja/nej —
                // {} = obesvarad, finalize-grinden kräver ett boolean-svar.
                revisor: typeof f.harRevisor === 'boolean'
                    ? {
                        harRevisor: f.harRevisor,
                        namn: f.harRevisor ? f.revisorNamn.trim() : '',
                        byra: f.harRevisor ? f.revisorByra.trim() : '',
                    }
                    : {},
                // ABL 25:13-intyget (kapitalbrist) — sparas bara som true;
                // avbockning tar bort det.
                kontrollbalans_attest: f.kontrollbalansAttest === true,
                // ÅRL 2:7-intyget: undertecknarlistan komplett.
                styrelse_komplett_attest: f.styrelseKomplettAttest === true,
            },
            board_members: f.boardMembers
                .filter((m) => m.name.trim() !== '')
                .map((m) => ({ name: m.name.trim(), role: m.role })),
            notes: {
                medelantal_anstallda: {
                    // TOMT är obesvarat (null) — ALDRIG en tyst nolla: finalize
                    // kräver ett svar och 0 måste vara ett medvetet val
                    // (YE-C-14; ÅRL 5:20). Filens egen kommentar sade detta
                    // redan — nu gör koden det också.
                    count: f.medelantalAnstallda.trim() !== '' && Number.isFinite(parseAmount(f.medelantalAnstallda))
                        ? parseAmount(f.medelantalAnstallda) : null,
                    source: suggestions?.medelantalAnstallda?.source || null,
                },
                // ÅRL 5:14: obligatorisk ja/nej; vid ja krävs belopp + art
                // och form (finalize-grinden). {} = obesvarad.
                stallda_sakerheter: typeof f.stalldaSakerheterFinns === 'boolean'
                    ? {
                        finns: f.stalldaSakerheterFinns,
                        amount: f.stalldaSakerheterFinns
                            && Number.isFinite(parseAmount(f.stalldaSakerheter))
                            ? parseAmount(f.stalldaSakerheter) : 0,
                        text: f.stalldaSakerheterFinns ? f.stalldaSakerheterText.trim() : '',
                    } : {},
                // ÅRL 5:15: obligatorisk ja/nej; vid ja krävs summan.
                eventualforpliktelser: typeof f.eventualforpliktelserFinns === 'boolean'
                    ? {
                        finns: f.eventualforpliktelserFinns,
                        amount: f.eventualforpliktelserFinns
                            && Number.isFinite(parseAmount(f.eventualforpliktelser))
                            ? parseAmount(f.eventualforpliktelser) : 0,
                        text: f.eventualforpliktelserFinns ? f.eventualforpliktelserText.trim() : '',
                    } : {},
                // ÅRL 5:13: 0 kr är ett giltigt, meningsfullt svar — bara ett
                // TOMT fält utelämnas (= obesvarat, finalize vägrar).
                langfristiga_skulder_5ar: f.langfristigaSkulder5ar.trim() !== ''
                    && Number.isFinite(parseAmount(f.langfristigaSkulder5ar))
                    ? { amount: parseAmount(f.langfristigaSkulder5ar) } : {},
                // ÅRL 5:18: frågan är alltid obligatorisk; text bara vid ja.
                lan_ledande: typeof f.lanLedandeFinns === 'boolean'
                    ? {
                        finns: f.lanLedandeFinns,
                        text: f.lanLedandeFinns ? f.lanLedandeText.trim() : '',
                    } : {},
                // ÅRL 5:8: överföringar mellan AT-poster — ja/nej + text vid ja.
                at_overforingar: typeof f.atOverforingarFinns === 'boolean'
                    ? {
                        finns: f.atOverforingarFinns,
                        text: f.atOverforingarFinns ? f.atOverforingarText.trim() : '',
                    } : {},
                // Rättelse av fel (nya K2 p. 2.12/18.7A) — nyckeln skrivs
                // BARA när frågan besvarats, så äldre års notes-JSON (FY2025)
                // förblir orörd.
                ...(typeof f.rattelseFelFinns === 'boolean'
                    ? {
                        rattelse_fel: {
                            finns: f.rattelseFelFinns,
                            text: f.rattelseFelFinns ? f.rattelseFelText.trim() : '',
                        },
                    } : {}),
                // ÅRL 5:21: obligatorisk fråga; namn/orgnr/säte krävs vid ja.
                moderforetag: typeof f.moderAr === 'boolean'
                    ? {
                        arDotterforetag: f.moderAr,
                        namn: f.moderAr ? f.moderNamn.trim() : '',
                        orgnr: f.moderAr ? f.moderOrgnr.trim() : '',
                        sate: f.moderAr ? f.moderSate.trim() : '',
                    } : {},
                goodwill: { text: f.goodwillText.trim() }, // ÅRL 5:9
                uppskrivningsfond: { text: f.uppskrivningsfondText.trim() }, // ÅRL 5:11
                // Egna noter — renderas sist i notmodellen (kräver både
                // rubrik och text för att tryckas).
                custom: f.customNotes
                    .map((c) => ({ title: c.title.trim(), text: c.text.trim() }))
                    .filter((c) => c.title !== '' || c.text !== ''),
                // ÅRL 5:22 resp. 5:19: obligatoriska ja/nej; text vid ja.
                handelser_efter_aret: typeof f.handelserEfterAretFinns === 'boolean'
                    ? {
                        finns: f.handelserEfterAretFinns,
                        text: f.handelserEfterAretFinns ? f.handelserEfterAret.trim() : '',
                    } : {},
                avskrivningsprinciper: { text: f.avskrivningsprinciper.trim() },
                andrade_principer: { text: f.andradePrinciper.trim() },
                exceptionella_poster: typeof f.exceptionellaPosterFinns === 'boolean'
                    ? {
                        finns: f.exceptionellaPosterFinns,
                        text: f.exceptionellaPosterFinns ? f.exceptionellaPoster.trim() : '',
                    } : {},
                poster_flera_poster: { text: f.posterFleraPoster.trim() },
                ekonomiska_arrangemang: { text: f.ekonomiskaArrangemang.trim() },
            },
        };
        const utd = parseOptionalAmount(f.utdelning);
        const bal = round2((f._till || 0) - utd);
        const antalForCheck = parseInt(f.antalAktier, 10);
        // ABL 18:3: per-aktie-beloppet måste gå jämnt ut i hela ören — en
        // ojämn delning hålls utanför patchen (backend 422:ar den annars och
        // autosaven skulle tappa övriga fält); inline-hinten förklarar.
        const jamnDelning = !(utd > 0 && Number.isInteger(antalForCheck) && antalForCheck > 0)
            || Math.round(utd * 100) % antalForCheck === 0;
        // An invalid disposition (utdelning > summan) is kept out of the
        // patch — the backend would 422 it; the inline hint explains.
        if (Number.isFinite(utd) && utd >= 0 && bal >= -0.005 && jamnDelning) {
            const antal = parseInt(f.antalAktier, 10);
            patch.resultatdisposition = {
                tillDisposition: round2(f._till || 0),
                utdelning: round2(utd),
                balanserasINyRakning: Math.max(0, bal),
                antalAktier: Number.isInteger(antal) && antal > 0 ? antal : null,
                forutsattningar: f.utdForutsattningar
                    ? { enAktieslag: true, kontantUtdelning: true, ejAvstamningsbolag: true }
                    : {},
            };
        }
        return patch;
    }, [suggestions]);

    const saveNow = useCallback(async () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        const patch = buildPatch();
        if (!patch) return;
        setSaveState('saving');
        setSaveError(null);
        try {
            const resp = await YearEndService.updateAnnualReport(companyId, processId, patch);
            if (resp?.data) setReport(resp.data);
            setSaveState('saved');
        } catch (err) {
            setSaveState('error');
            setSaveError(err.response?.data?.message
                || 'Kunde inte spara ändringarna. Försök igen.');
        }
    }, [buildPatch, companyId, processId]);

    const scheduleSave = useCallback(() => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { saveNow(); }, 600);
    }, [saveNow]);

    useEffect(() => () => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
    }, []);

    const setField = (field) => (e) => {
        const { value } = e.target;
        setForm((prev) => ({ ...prev, [field]: value }));
        setFinalizeProblems(null);
    };

    // OBS: inget antal-år-val för flerårsöversikten — K2 p. 5.5 är ett SKA
    // ("räkenskapsåret och de tre föregående åren"); färre år visas bara när
    // historiken faktiskt saknas (Codex-fynd 2026-08-11, samma dag som valet
    // kortvarigt fanns).

    const setBoardField = (index, field) => (e) => {
        const { value } = e.target;
        setForm((prev) => ({
            ...prev,
            boardMembers: prev.boardMembers.map((m, i) => (
                i === index ? { ...m, [field]: value } : m
            )),
        }));
        setFinalizeProblems(null);
    };

    const addBoardMember = () => {
        setForm((prev) => ({
            ...prev,
            boardMembers: [...prev.boardMembers, { name: '', role: 'ledamot' }],
        }));
    };

    const removeBoardMember = (index) => {
        setForm((prev) => ({
            ...prev,
            boardMembers: prev.boardMembers.filter((_, i) => i !== index),
        }));
        scheduleSave();
    };

    /** Ja/nej-svar (ÅRL 5:18/5:21, revisorsfrågan) — sparas direkt. */
    const answerBool = (field) => (value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setFinalizeProblems(null);
        scheduleSave();
    };

    const setCustomNote = (index, field) => (e) => {
        const { value } = e.target;
        setForm((prev) => ({
            ...prev,
            customNotes: prev.customNotes.map((c, i) => (
                i === index ? { ...c, [field]: value } : c
            )),
        }));
    };

    const addCustomNote = () => {
        setForm((prev) => ({
            ...prev,
            customNotes: [...prev.customNotes, { title: '', text: '' }],
        }));
    };

    const removeCustomNote = (index) => {
        setForm((prev) => ({
            ...prev,
            customNotes: prev.customNotes.filter((_, i) => i !== index),
        }));
        scheduleSave();
    };

    // ---- Actions ----------------------------------------------------------

    const handleOpenPdf = async () => {
        setOpeningPdf(true);
        try {
            if (!readOnly) await saveNow();
            await YearEndService.openAnnualReportPdf(companyId, processId);
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ta fram PDF-förhandsgranskningen.');
        } finally {
            setOpeningPdf(false);
        }
    };

    const handleFinalize = async () => {
        setFinalizing(true);
        setFinalizeProblems(null);
        setFinalizeError(null);
        try {
            await saveNow();
            const resp = await YearEndService.finalizeAnnualReport(companyId, processId);
            toast.success('Årsredovisningen är färdigställd och klar för underskrift.');
            if (resp?.data) {
                setReport(resp.data);
                setEditing(false);
                // Notmodellen (numrering, radhänvisningar) byggs på servern —
                // hämta om den tyst så läsvyn visar det som faktiskt trycks.
                fetchReport({ silent: true });
            } else {
                await fetchReport({ silent: true });
            }
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 409 && Array.isArray(data?.details?.problems)) {
                setFinalizeProblems(data.details.problems);
                setFinalizeError(data?.message || 'Årsredovisningen är inte komplett ännu.');
            } else {
                setFinalizeError(data?.message || 'Kunde inte färdigställa årsredovisningen.');
            }
        } finally {
            setFinalizing(false);
        }
    };

    // ---- Dokumentbyggstenar ------------------------------------------------

    // OBS: Zone/ProseZone/StatementTable är HISSADE till modulnivå (nedanför
    // komponenten). Definierade här inne fick de ny komponentidentitet varje
    // render — React monterade om varje input per tangenttryck och fokus
    // försvann efter en bokstav (user find 2026-08-11). De binds till stegets
    // state via props-baggen nedan.
    const zoneCtx = { readOnly };
    const proseCtx = { form, readOnly, setField, scheduleSave };

    // Dokumenttabeller i hela kronor utan enhet (framsidan deklarerar "alla
    // belopp i hela kronor") — dispositionen visar öre eftersom stämman
    // beslutar om de exakta beloppen.
    const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString('sv-SE'));
    const sek = (v) => (v == null ? '—' : formatSek(v));
    // Flerårsöversiktens enhet väljs av backend efter beloppens storlek
    // (kr / Tkr / Mkr) så att dokumentvyn och PDF:en alltid stämmer överens.
    const flerarsUnit = model?.forvaltningsberattelse?.flerarsUnit || 'kr';
    const flerarsUnitLabel = { kr: 'kr', tkr: 'Tkr', mkr: 'Mkr' }[flerarsUnit] || 'kr';
    const flerarsDiv = { kr: 1, tkr: 1e3, mkr: 1e6 }[flerarsUnit] || 1;
    const tkr = (v) => (v == null ? '—' : Math.round(v / flerarsDiv).toLocaleString('sv-SE'));

    const tableYear = model?.fiscalYear?.year ?? '';

    // ---- Dokumentsektioner -------------------------------------------------

    const renderFramsida = () => (
        <header className="ard-front">
            <div className="ard-title-page">
                <div className="ard-doc-type">Årsredovisning</div>
                <div className="ard-for">för</div>
                <div className="ard-company">{model?.company?.company_name || '…'}</div>
                <div className="ard-meta">
                    {(() => {
                        const raw = String(model?.company?.registration_number || '');
                        if (!raw) return '—';
                        return /^\d{10}$/.test(raw) ? `Org.nr ${raw.slice(0, 6)}-${raw.slice(6)}` : `Org.nr ${raw}`;
                    })()}
                </div>
                <div className="ard-meta ard-rakenskapsar">Räkenskapsåret</div>
                <div className="ard-meta">
                    {isoDate(model?.fiscalYear?.start_date)} – {isoDate(model?.fiscalYear?.end_date)}
                </div>
            </div>
            <p className="ard-p">
                Styrelsen avger härmed följande årsredovisning. Om inte annat särskilt
                anges redovisas alla belopp i hela kronor.
            </p>
        </header>
    );

    const renderForvaltningsberattelse = () => {
        const flerars = model?.forvaltningsberattelse?.flerarsoversikt || [];
        const variation = Boolean(model?.forvaltningsberattelse?.flerarsVariationOver30);
        const ek = model?.forvaltningsberattelse?.equityChanges;
        return (
            <section>
                <h2 className="ard-h1">Förvaltningsberättelse</h2>

                <h3 className="ard-h2">Verksamheten</h3>
                <ProseZone {...proseCtx}
                    field="verksamhet"
                    required
                    placeholder="Beskriv vad företaget gör — en eller ett par meningar räcker. T.ex. 'Bolaget bedriver konsultverksamhet inom IT.'"
                    ariaLabel="Allmänt om verksamheten"
                />
                {!readOnly && form.verksamhet.trim() === ''
                    && (suggestions?.verksamhetForslag || []).length > 0 && (
                    <div className="ard-forslag">
                        {suggestions.verksamhetForslag.map((f) => (
                            <button
                                key={f.source}
                                type="button"
                                className="ard-forslag-chip"
                                onClick={() => {
                                    setField('verksamhet')({ target: { value: f.text } });
                                    scheduleSave();
                                }}
                            >
                                <span className="ard-forslag-use">Använd förslag</span>
                                &ldquo;{f.text.length > 140 ? `${f.text.slice(0, 140)}…` : f.text}&rdquo;
                                <span className="ard-forslag-src">{f.source}</span>
                            </button>
                        ))}
                    </div>
                )}
                <div className="ard-p ard-sate-line">
                    {readOnly ? (
                        form.sate.trim() ? `Styrelsen har sitt säte i ${form.sate}.` : null
                    ) : (
                        <Zone {...zoneCtx} needsInput={form.sate.trim() === ''}>
                            {/* Samma lydelse som PDF:en (ArPdfService) och
                                iXBRL-dokumentet trycker — dokumentvyn ska
                                visa det som faktiskt skrivs ut. */}
                            <span>Styrelsen har sitt säte i{' '}</span>
                            <input
                                className="ard-inline-input"
                                type="text"
                                placeholder="kommun"
                                aria-label="Styrelsens säte"
                                value={form.sate}
                                onChange={setField('sate')}
                                onBlur={scheduleSave}
                                style={{ width: `${Math.max(6, form.sate.length + 2)}ch` }}
                            />
                            <span>.</span>
                            {suggestions?.sate?.value && form.sate === suggestions.sate.value && (
                                <span className="ard-hint-inline"> (från Bolagsverket)</span>
                            )}
                        </Zone>
                    )}
                </div>

                {(!readOnly || form.vasentligaHandelser.trim()) && (
                    <>
                        <h3 className="ard-h2">Väsentliga händelser under räkenskapsåret</h3>
                        <ProseZone {...proseCtx}
                            field="vasentligaHandelser"
                            placeholder="Större saker som påverkat företaget. Kan lämnas tom om inget särskilt hänt — rubriken skrivs då inte ut."
                            ariaLabel="Väsentliga händelser under räkenskapsåret"
                        />
                    </>
                )}

                <h3 className="ard-h2">Flerårsöversikt <span className="ard-unit">({flerarsUnitLabel})</span></h3>
                <div className="ard-table-wrap">
                    <table className="ard-table">
                        <thead>
                            <tr>
                                <th aria-label="Nyckeltal" />
                                {flerars.map((r) => <th key={r.year} className="ard-num">{r.year}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Nettoomsättning</td>
                                {flerars.map((r) => <td key={r.year} className="ard-num">{tkr(r.nettoomsattning)}</td>)}
                            </tr>
                            <tr>
                                <td>Resultat efter finansiella poster</td>
                                {flerars.map((r) => <td key={r.year} className="ard-num">{tkr(r.resultatEfterFinansiellaPoster)}</td>)}
                            </tr>
                            <tr>
                                <td>Soliditet (%)</td>
                                {flerars.map((r) => (
                                    <td key={r.year} className="ard-num">
                                        {r.soliditetPct == null ? '—' : String(r.soliditetPct).replace('.', ',')}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="ard-p">För definitioner av nyckeltal, se Not 1 Redovisnings- och värderingsprinciper.</p>
                {flerars.length < 2 && (
                    <p className="ard-hint">
                        Endast ett år finns i systemet ännu — jämförelseår fylls på i takt
                        med att fler räkenskapsår avslutas.
                    </p>
                )}
                {(variation || form.flerarsKommentar.trim() || !readOnly) && (
                    <ProseZone {...proseCtx}
                        field="flerarsKommentar"
                        required={variation}
                        placeholder={variation
                            ? 'Nettoomsättningen varierar mer än 30 % mellan åren — K2 kräver en kommentar. Förklara varför.'
                            : 'Frivillig kommentar till flerårsöversikten.'}
                        ariaLabel="Kommentar till flerårsöversikten"
                    />
                )}

                {ek?.rows?.length > 0 && (
                    <>
                        <h3 className="ard-h2">Förändringar i eget kapital</h3>
                        <div className="ard-table-wrap">
                            <table className="ard-table">
                                <thead>
                                    <tr>
                                        <th aria-label="Händelse" />
                                        {ek.columns.map((c) => <th key={c.key} className="ard-num">{c.label}</th>)}
                                        <th className="ard-num">Totalt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ek.rows.map((r, i) => {
                                        const firstDisposition = r.group === 'disposition'
                                            && ek.rows.findIndex((x) => x.group === 'disposition') === i;
                                        return (
                                            <React.Fragment key={r.key}>
                                                {firstDisposition && (
                                                    <tr><td colSpan={ek.columns.length + 2}>Disposition enligt beslut av årsstämman:</td></tr>
                                                )}
                                                <tr className={r.isTotal ? 'ard-sum' : undefined}>
                                                    <td className={r.group === 'disposition' ? 'ard-indent' : undefined}>{r.label}</td>
                                                    {ek.columns.map((c) => (
                                                        <td key={c.key} className="ard-num">
                                                            {r.values[c.key] ? num(r.values[c.key]) : ''}
                                                        </td>
                                                    ))}
                                                    <td className="ard-num">{num(r.total)}</td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                <h3 className="ard-h2">Resultatdisposition</h3>
                <p className="ard-p">Styrelsen föreslår att till förfogande stående vinstmedel (kronor):</p>
                <div className="ard-table-wrap">
                    <table className="ard-table">
                        <tbody>
                            <tr>
                                <td>Balanserat resultat</td>
                                <td className="ard-num">{sek(dispBasis?.balanseratResultat)}</td>
                            </tr>
                            <tr>
                                <td>Årets resultat</td>
                                <td className="ard-num">{sek(dispBasis?.aretsResultat)}</td>
                            </tr>
                            <tr className="ard-sum">
                                <td>Summa</td>
                                <td className="ard-num">{sek(till)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="ard-p">disponeras så att:</p>
                <div className="ard-table-wrap">
                    <table className="ard-table">
                        <tbody>
                            <tr>
                                <td>Till aktieägare utdelas</td>
                                <td className="ard-num">
                                    {readOnly ? sek(utdelningValid ? utdelningValue : 0) : (
                                        <Zone {...zoneCtx} needsInput={false}>
                                            <input
                                                className="ard-inline-input ard-num-input"
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0"
                                                aria-label="Utdelning till aktieägarna (kr)"
                                                value={form.utdelning}
                                                onChange={setField('utdelning')}
                                                onBlur={scheduleSave}
                                            />
                                        </Zone>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>I ny räkning överföres</td>
                                <td className="ard-num">{balanseras != null ? sek(Math.max(0, balanseras)) : '—'}</td>
                            </tr>
                            <tr className="ard-sum">
                                <td>Summa</td>
                                <td className="ard-num">{sek(till)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                {!dispositionOk && (
                    <div className="alert alert-danger mt-2 mb-0" style={{ fontSize: '0.8125rem', maxWidth: 560 }}>
                        Utdelningen kan inte vara större än summan till förfogande ({formatSek(till)}).
                    </div>
                )}
                {/* 3:12-guiden (2026-08-11): kapacitetsmätare + optimal-knapp
                    vid beslutet — ren vägledning, aldrig en spärr. */}
                {!readOnly && (
                    <UtdelningsGuide
                        guide={suggestions?.utdelningsSkatt}
                        utdelning={utdelningValid ? utdelningValue : 0}
                        till={till}
                        companyId={companyId}
                        onApplyOptimal={(amount) => {
                            setForm((prev) => ({ ...prev, utdelning: String(amount) }));
                            setFinalizeProblems(null);
                            setTimeout(() => { saveNow(); }, 0);
                        }}
                    />
                )}
                {utdelningValid && utdelningValue > 0 && (
                    <>
                        {/* ABL 18:3: förslaget SKA ange belopp per aktie. */}
                        <div className="ard-p">
                            {readOnly ? (
                                (() => {
                                    const antal = parseInt(form.antalAktier, 10);
                                    if (!Number.isInteger(antal) || antal < 1) return null;
                                    return `Utdelningen motsvarar ${perAktieStr(utdelningValue, antal)} kr per aktie (${antal.toLocaleString('sv-SE')} aktier).`;
                                })()
                            ) : (
                                <Zone {...zoneCtx} needsInput={!(parseInt(form.antalAktier, 10) > 0)}>
                                    <span>Utdelningen fördelas på{' '}</span>
                                    <input
                                        className="ard-inline-input"
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="antal"
                                        aria-label="Antal aktier i bolaget"
                                        value={form.antalAktier}
                                        onChange={setField('antalAktier')}
                                        onBlur={scheduleSave}
                                        style={{ width: `${Math.max(6, form.antalAktier.length + 2)}ch` }}
                                    />
                                    <span>
                                        {' '}aktier
                                        {parseInt(form.antalAktier, 10) > 0
                                            ? ` — ${perAktieStr(utdelningValue, parseInt(form.antalAktier, 10))} kr per aktie.`
                                            : ' (krävs enligt ABL 18 kap. 3 § — belopp per aktie).'}
                                    </span>
                                </Zone>
                            )}
                        </div>
                        {/* Ojämn delning: förslaget ska ange belopp per aktie i hela
                            ören (ABL 18:3) — annars trycks "33,33 kr" × 3 ≠ 100 kr. */}
                        {!readOnly && (() => {
                            const antal = parseInt(form.antalAktier, 10);
                            if (!(Number.isInteger(antal) && antal > 0 && utdelningValue > 0)) return null;
                            const ore = Math.round(utdelningValue * 100);
                            if (ore % antal === 0) return null;
                            const ned = Math.floor(ore / antal);
                            return (
                                <p className="ard-p" style={{ color: '#b45309' }}>
                                    Utdelningen går inte jämnt ut på {antal.toLocaleString('sv-SE')} aktier
                                    — belopp per aktie ska anges i hela ören (ABL 18 kap. 3 §). Närmaste
                                    giltiga totalbelopp: {((ned * antal) / 100).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr
                                    {' '}eller {(((ned + 1) * antal) / 100).toLocaleString('sv-SE', { minimumFractionDigits: 2 })} kr.
                                </p>
                            );
                        })()}
                        {/* Förutsättningsintyg (ABL 18:3 andra-tredje styckena):
                            sakutdelning, flera aktieslag och avstämningsbolag stöds
                            inte — servern vägrar färdigställa utan intyget. */}
                        {(!readOnly || form.utdForutsattningar) && (
                            <div className="ard-p">
                                {readOnly ? (
                                    <span style={{ color: '#555' }}>
                                        Styrelsen har bekräftat: ett aktieslag med lika rätt till utdelning,
                                        kontant utdelning, ej avstämningsbolag.
                                    </span>
                                ) : (
                                    <Zone {...zoneCtx} needsInput={!form.utdForutsattningar}>
                                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={form.utdForutsattningar}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setForm((prev) => ({ ...prev, utdForutsattningar: checked }));
                                                    scheduleSave();
                                                }}
                                            />
                                            <span>
                                                Bolaget har ett aktieslag med lika rätt till utdelning, utdelningen
                                                sker kontant och bolaget är inte avstämningsbolag. (Sakutdelning,
                                                flera aktieslag eller avstämningsbolag kräver uppgifter enligt
                                                ABL 18 kap. 3 § som guiden inte stödjer — upprätta i så fall
                                                förslaget med rådgivare.)
                                            </span>
                                        </label>
                                    </Zone>
                                )}
                            </div>
                        )}
                        <p className="ard-p">
                            Styrelsen föreslås bemyndigas att besluta om tidpunkt då utdelningen
                            skall betalas.
                        </p>
                        {/* C2 (2026-08-11): ABL 18:4-yttrandet författas här, med
                            FÖRSLAGET — så protokollets "framlades" blir bokstavligt
                            sant. Stämmosteget förifylls från detta fält. */}
                        {(!readOnly || form.yttrande18_4.trim()) && (
                            <>
                                <h3 className="ard-h2">Styrelsens yttrande enligt 18 kap. 4 § aktiebolagslagen</h3>
                                {readOnly ? (
                                    <p className="ard-p">{form.yttrande18_4}</p>
                                ) : (
                                    <Zone {...zoneCtx} needsInput={form.yttrande18_4.trim() === ''}>
                                        <textarea
                                            className="ard-textarea"
                                            rows={4}
                                            aria-label="Styrelsens yttrande enligt ABL 18 kap. 4 §"
                                            placeholder="Klicka för att fylla i ett förslag…"
                                            value={form.yttrande18_4}
                                            onChange={setField('yttrande18_4')}
                                            onFocus={() => {
                                                if (!form.yttrande18_4.trim()) {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        yttrande18_4:
                                                            'Styrelsen bedömer att den föreslagna utdelningen om '
                                                            + `${formatSek(utdelningValue)} är försvarlig med hänsyn till de krav `
                                                            + 'som verksamhetens art, omfattning och risker ställer på storleken av '
                                                            + 'det egna kapitalet samt bolagets konsolideringsbehov, likviditet och '
                                                            + 'ställning i övrigt (17 kap. 3 § aktiebolagslagen). Full täckning finns '
                                                            + 'för bolagets bundna egna kapital efter utdelningen.',
                                                    }));
                                                }
                                            }}
                                            onBlur={scheduleSave}
                                        />
                                    </Zone>
                                )}
                            </>
                        )}
                    </>
                )}
                <p className="ard-p">
                    Företagets resultat och ställning i övrigt framgår av efterföljande
                    resultat- och balansräkning med noter.
                </p>
            </section>
        );
    };

    const renderResultatrakning = () => {
        const rr = model?.resultatrakning;
        if (!rr) return null;
        const prevYear = model?.previousFiscalYear?.year;
        // ÅRL 5:3: notkolumn vid de poster noterna hänför sig till — samma
        // rowRefs som PDF:n (rr nycklas på radens key), kolumnen finns bara
        // när det finns någon hänvisning.
        const rrRefs = model?.noterModel?.rowRefs?.rr || {};
        const noteCol = Object.keys(rrRefs).length > 0;
        const cols = noteCol ? 4 : 3;
        return (
            <section>
                <h2 className="ard-h1">Resultaträkning</h2>
                <StatementTable year={tableYear} prevYear={prevYear ?? '—'} noteCol={noteCol}>
                    {rr.sections.map((s) => (
                        s.isSummering ? (
                            <tr className="ard-sum" key={s.key}>
                                <td>{s.label}</td>
                                {noteCol && <td />}
                                <td className="ard-num">{num(s.subtotal)}</td>
                                <td className="ard-num">{prevYear != null ? num(s.prevSubtotal ?? 0) : '—'}</td>
                            </tr>
                        ) : (
                            <React.Fragment key={s.key}>
                                <tr className="ard-group"><td colSpan={cols}>{s.label}</td></tr>
                                {(s.rows || []).map((r) => (
                                    <tr key={r.key || r.label}>
                                        <td className="ard-indent">{r.label}</td>
                                        {noteCol && (
                                            <td className="ard-note-ref">{(rrRefs[r.key] || []).join(', ')}</td>
                                        )}
                                        <td className="ard-num">{num(r.amount)}</td>
                                        <td className="ard-num">{prevYear != null ? num(r.prevAmount ?? 0) : '—'}</td>
                                    </tr>
                                ))}
                                {s.sumLabel && (
                                    <tr className="ard-subsum">
                                        <td>{s.sumLabel}</td>
                                        {noteCol && <td />}
                                        <td className="ard-num">{num(s.subtotal)}</td>
                                        <td className="ard-num">{prevYear != null ? num(s.prevSubtotal ?? 0) : '—'}</td>
                                    </tr>
                                )}
                            </React.Fragment>
                        )
                    ))}
                </StatementTable>
            </section>
        );
    };

    const renderBalansSide = (title, groups, total, prevTotal, sumLabel, prevYear, brRefs, noteCol) => (
        <>
            <h3 className="ard-h2 ard-br-side">{title}</h3>
            <StatementTable year={tableYear} prevYear={prevYear ?? '—'} noteCol={noteCol}>
                {(groups || []).map((g) => (
                    g.rows ? (
                        <React.Fragment key={g.label}>
                            <tr className="ard-group"><td colSpan={noteCol ? 4 : 3}>{g.label}</td></tr>
                            {g.rows.map((r) => (
                                <tr key={r.label}>
                                    <td className="ard-indent">{r.label}</td>
                                    {noteCol && (
                                        <td className="ard-note-ref">{(brRefs[r.label] || []).join(', ')}</td>
                                    )}
                                    <td className="ard-num">{num(r.amount)}</td>
                                    <td className="ard-num">{prevYear != null ? num(r.prevAmount ?? 0) : '—'}</td>
                                </tr>
                            ))}
                            <tr className="ard-subsum">
                                <td>{g.sumLabel}</td>
                                {noteCol && <td />}
                                <td className="ard-num">{num(g.subtotal)}</td>
                                <td className="ard-num">{prevYear != null ? num(g.prevSubtotal ?? 0) : '—'}</td>
                            </tr>
                        </React.Fragment>
                    ) : (
                        <tr key={g.label}>
                            <td>{g.label}</td>
                            {noteCol && (
                                <td className="ard-note-ref">{(brRefs[g.label] || []).join(', ')}</td>
                            )}
                            <td className="ard-num">{num(g.amount)}</td>
                            <td className="ard-num">{prevYear != null ? num(g.prevAmount ?? 0) : '—'}</td>
                        </tr>
                    )
                ))}
                <tr className="ard-sum">
                    <td>{sumLabel}</td>
                    {noteCol && <td />}
                    <td className="ard-num">{num(total)}</td>
                    <td className="ard-num">{prevTotal != null ? num(prevTotal) : '—'}</td>
                </tr>
            </StatementTable>
        </>
    );

    const renderBalansrakning = () => {
        const br = model?.balansrakning;
        if (!br) return null;
        const prevYear = model?.previousFiscalYear?.year;
        // ÅRL 5:3 — br nycklas på radens/postens ETIKETT (samma som PDF:n).
        const brRefs = model?.noterModel?.rowRefs?.br || {};
        const noteCol = Object.keys(brRefs).length > 0;
        return (
            <section>
                <h2 className="ard-h1">Balansräkning <span className="ard-unit">Balansdag {isoDate(model?.fiscalYear?.end_date)}</span></h2>
                {renderBalansSide('Tillgångar', br.assets, br.totalAssets,
                    prevYear != null ? (br.prevTotalAssets ?? 0) : null, 'Summa tillgångar', prevYear, brRefs, noteCol)}
                {renderBalansSide('Eget kapital och skulder', br.equityLiabilities, br.totalEquityAndLiabilities,
                    prevYear != null ? (br.prevTotalEquityAndLiabilities ?? 0) : null,
                    'Summa eget kapital och skulder', prevYear, brRefs, noteCol)}
            </section>
        );
    };

    // Ställda säkerheter/eventualförpliktelser/händelser efter året/
    // exceptionella poster är sedan Codex-granskning 6 OBLIGATORISKA
    // ja/nej-frågor (egna zoner nedan) — kvar här är de genuint frivilliga.
    const OPTIONAL_NOTES = [
        ['andradePrinciper', 'Ändrade redovisningsprinciper', 'Endast om företaget bytt princip — beskriv ändringen och effekten.', 'text'],
        ['posterFleraPoster', 'Tillgångar, avsättningar och skulder som avser flera poster', 'Om ett belopp i balansräkningen fördelar sig på flera poster (ÅRL 3:4).', 'text'],
        ['ekonomiskaArrangemang', 'Ekonomiska arrangemang som inte redovisas i balansräkningen', 'T.ex. leasing eller factoring med väsentliga risker utanför balansräkningen.', 'text'],
    ];

    /** Formulärfält → notnyckel i serverns notmodell (numreringen). */
    const NOTE_KEY_BY_FIELD = {
        stalldaSakerheter: 'stallda_sakerheter',
        eventualforpliktelser: 'eventualforpliktelser',
        handelserEfterAret: 'handelser_efter_aret',
        andradePrinciper: 'andrade_principer',
        exceptionellaPoster: 'exceptionella_poster',
        posterFleraPoster: 'poster_flera_poster',
        ekonomiskaArrangemang: 'ekonomiska_arrangemang',
    };

    // Hårdkodade nyckeltalsdefinitioner — samma text som PDF:ns Not 1.
    const nyckeltalsDefinitioner = (
        <>
            <h4 className="ard-h3">Nyckeltalsdefinitioner</h4>
            <p className="ard-p">
                Nettoomsättning: Rörelsens huvudintäkter, fakturerade kostnader,
                sidointäkter samt intäktskorrigeringar.
            </p>
            <p className="ard-p">
                Resultat efter finansiella poster: Resultat efter finansiella intäkter
                och kostnader men före bokslutsdispositioner och skatter.
            </p>
            <p className="ard-p">
                Soliditet (%): Justerat eget kapital (eget kapital och obeskattade
                reserver med avdrag för uppskjuten skatt) i procent av balansomslutning.
            </p>
        </>
    );

    /** Anläggningsnot (ÅRL 5:8): liten högerställd beloppstabell. */
    const renderAnlaggningTable = (note) => (
        <div className="ard-table-wrap">
            <table className="ard-table ard-note-table">
                <tbody>
                    {(note.rows || []).map((r, i) => (
                        <tr key={i} className={r.rule ? (r.strong ? 'ard-sum' : 'ard-subsum') : undefined}>
                            <td style={r.bold && !r.rule ? { fontWeight: 700 } : undefined}>{r.label}</td>
                            <td className="ard-num" style={r.bold && !r.rule ? { fontWeight: 700 } : undefined}>{num(r.amount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderNoter = () => {
        const noterM = model?.noterModel || null;
        const conditions = noterM?.conditions || {};
        const nrByKey = {};
        (noterM?.notes || []).forEach((n) => { nrByKey[n.key] = n.nr; });
        // Rubrik med serverns nummer när noten finns i dokumentet; utan
        // nummer tills den fyllts i (numreringen sätts av notmodellen och
        // kan inte förutses för en ännu inte utskriven not).
        const notTitle = (key, title) => (nrByKey[key] ? `Not ${nrByKey[key]} — ${title}` : title);

        // —— Läs-läge: dokumentet renderas RAKT UR serverns notmodell — samma
        // numrering, ordning och innehåll som PDF:n trycker (ÅRL 5:3). ——
        if (readOnly && noterM?.notes?.length) {
            return (
                <section>
                    <h2 className="ard-h1">Noter</h2>
                    {noterM.notes.map((note) => (
                        <React.Fragment key={note.key}>
                            <h3 className="ard-h2">Not {note.nr} — {note.title}</h3>
                            {note.kind === 'principer' && (
                                <>
                                    <p className="ard-p">{note.text}</p>
                                    {note.avskrivningsprinciper && (
                                        <>
                                            <h4 className="ard-h3">Avskrivningsprinciper</h4>
                                            <p className="ard-p">{note.avskrivningsprinciper}</p>
                                        </>
                                    )}
                                    {nyckeltalsDefinitioner}
                                </>
                            )}
                            {note.kind === 'medelantal' && (
                                <p className="ard-p">Medelantalet anställda under räkenskapsåret var {note.count}.</p>
                            )}
                            {note.kind === 'anlaggning' && (
                                <>
                                    {renderAnlaggningTable(note)}
                                    {note.goodwillText && <p className="ard-p">{note.goodwillText}</p>}
                                </>
                            )}
                            {note.kind === 'text' && <p className="ard-p">{note.text}</p>}
                        </React.Fragment>
                    ))}
                </section>
            );
        }

        // —— Redigeringsläge (samt läs-läge UTAN notmodell — äldre cachat
        // svar: då gäller den tidigare klientnumreringen som förut). ——
        const filledOptional = OPTIONAL_NOTES.filter(([field]) => form[field].trim() !== '');
        const emptyOptional = OPTIONAL_NOTES.filter(([field]) => form[field].trim() === '');
        let notNr = 2;
        const optionalHeading = (field, title) => {
            if (noterM) return notTitle(NOTE_KEY_BY_FIELD[field], title);
            notNr += 1;
            return `Not ${notNr} — ${title}`;
        };

        // 'sakerheter'/'forpliktelser'-varianterna flyttade till de
        // obligatoriska JaNejZone-blocken ovan (Codex-granskning 6) — kvar
        // är ren prosa för de genuint frivilliga noterna.
        const renderOptionalBody = (field, title, help) => (
            <ProseZone {...proseCtx} field={field} placeholder={help} ariaLabel={title} />
        );

        const anlaggningNotes = (noterM?.notes || []).filter((n) => n.kind === 'anlaggning');
        const goodwillZone = (
            /* ÅRL 5:9 — goodwill: tillämpad avskrivningsperiod och skälen. */
            <Zone {...zoneCtx} needsInput={form.goodwillText.trim() === ''}>
                <textarea
                    className="ard-textarea"
                    rows={2}
                    placeholder='Goodwill: tillämpad avskrivningsperiod och skälen för den (ÅRL 5:9) — t.ex. "Goodwill skrivs av över 5 år, motsvarande bedömd nyttjandeperiod."'
                    aria-label="Goodwill — avskrivningsperiod och skäl"
                    value={form.goodwillText}
                    onChange={setField('goodwillText')}
                    onBlur={scheduleSave}
                />
            </Zone>
        );

        return (
            <section>
                <h2 className="ard-h1">Noter</h2>

                <h3 className="ard-h2">Not {nrByKey.redovisningsprinciper || 1} — Redovisnings- och värderingsprinciper</h3>
                <p className="ard-p">
                    Årsredovisningen är upprättad i enlighet med årsredovisningslagen och
                    BFN:s allmänna råd (K2) om årsredovisning i mindre företag.
                </p>
                <ProseZone {...proseCtx}
                    field="avskrivningsprinciper"
                    placeholder="Har företaget anläggningstillgångar: beskriv avskrivningsmetod och nyttjandeperioder, t.ex. 'Inventarier skrivs av linjärt över 5 år.' Annars kan fältet lämnas tomt."
                    ariaLabel="Avskrivningsprinciper"
                />
                {nyckeltalsDefinitioner}

                <h3 className="ard-h2">
                    {noterM ? notTitle('medelantal_anstallda', 'Medelantalet anställda') : 'Not 2 — Medelantal anställda'}
                </h3>
                <div className="ard-p">
                    {readOnly ? (
                        <>Medelantalet anställda under räkenskapsåret var {form.medelantalAnstallda || '—'}.</>
                    ) : (
                        <Zone {...zoneCtx} needsInput={form.medelantalAnstallda.trim() === ''}>
                            <span>Medelantalet anställda under räkenskapsåret var{' '}</span>
                            <input
                                className="ard-inline-input ard-num-input"
                                type="text"
                                inputMode="numeric"
                                aria-label="Medelantal anställda"
                                value={form.medelantalAnstallda}
                                onChange={setField('medelantalAnstallda')}
                                onBlur={scheduleSave}
                                style={{ width: '4ch' }}
                            />
                            <span>.</span>
                            {suggestions?.medelantalAnstallda?.source
                                && suggestions?.medelantalAnstallda?.count != null && (
                                <span className="ard-hint-inline"> Förslag: {suggestions.medelantalAnstallda.count} ({suggestions.medelantalAnstallda.source})</span>
                            )}
                            {' '}
                            <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement="top"
                                overlay={(
                                    <Popover id="ard-help-medelantal" style={{ maxWidth: 360 }}>
                                        <Popover.Header as="h3" style={{ fontSize: '0.8125rem' }}>
                                            Medelantalet anställda (ÅRL 5:20)
                                        </Popover.Header>
                                        <Popover.Body style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>
                                            Obligatorisk upplysning för alla företag — även 0 anges.
                                            BFNAR 2006:11 medger tre metoder: heltidstjänster på
                                            individnivå (p. 7), arbetade timmar delat med normal
                                            årsarbetstid, förenklat 1 920 h (p. 8–9), eller genomsnitt
                                            av antalet anställda vid minst två representativa
                                            mätpunkter (p. 10). Förslaget använder p. 10: antal
                                            personer med lönebesked per månad, i genomsnitt över
                                            räkenskapsåret. Deltid räknas då per person — har
                                            företaget många deltidare ger timmetoden ett lägre,
                                            mer rättvisande tal: justera fältet.
                                        </Popover.Body>
                                    </Popover>
                                )}
                            >
                                <button type="button" className="ard-info-btn" aria-label="Hjälp: medelantalet anställda">
                                    <Info size={14} />
                                </button>
                            </OverlayTrigger>
                        </Zone>
                    )}
                </div>

                {/* Anläggningsnoter (ÅRL 5:8) — härledda ur bokföringen och
                    läs-endast; visas även vid redigering så numreringen känns
                    igen från det tryckta dokumentet. */}
                {anlaggningNotes.map((note) => (
                    <React.Fragment key={note.key}>
                        <h3 className="ard-h2">Not {note.nr} — {note.title}</h3>
                        {renderAnlaggningTable(note)}
                        {note.key === 'immateriella_at' && !readOnly && conditions.hasGoodwill && goodwillZone}
                    </React.Fragment>
                ))}
                {!readOnly && conditions.hasGoodwill
                    && !anlaggningNotes.some((n) => n.key === 'immateriella_at') && (
                    <>
                        <h3 className="ard-h2">Goodwill</h3>
                        {goodwillZone}
                    </>
                )}

                {/* Uppskrivningsfond (ÅRL 5:11) — bara när konto 2085 har
                    saldo eller rörelse. */}
                {!readOnly && conditions.hasUppskrivningsfond && (
                    <>
                        <h3 className="ard-h2">{notTitle('uppskrivningsfond', 'Uppskrivningsfond')}</h3>
                        <Zone {...zoneCtx} needsInput={form.uppskrivningsfondText.trim() === ''}>
                            <textarea
                                className="ard-textarea"
                                rows={2}
                                placeholder="Fondens storlek vid årets början och slut samt årets förändringar (ÅRL 5:11)."
                                aria-label="Uppskrivningsfond"
                                value={form.uppskrivningsfondText}
                                onChange={setField('uppskrivningsfondText')}
                                onBlur={scheduleSave}
                            />
                        </Zone>
                    </>
                )}

                {/* Långfristiga skulder med förfall > 5 år (ÅRL 5:13) —
                    obligatorisk så snart långfristiga skulder finns. */}
                {!readOnly && conditions.hasLangfristigaSkulder && (
                    <>
                        <h3 className="ard-h2">{notTitle('langfristiga_skulder_5ar', 'Långfristiga skulder')}</h3>
                        <div className="ard-p">
                            <Zone {...zoneCtx} needsInput={form.langfristigaSkulder5ar.trim() === ''}>
                                <span>Av de långfristiga skulderna förfaller{' '}</span>
                                <input
                                    className="ard-inline-input ard-num-input"
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="belopp"
                                    aria-label="Långfristiga skulder som förfaller senare än fem år efter balansdagen (kr)"
                                    value={form.langfristigaSkulder5ar}
                                    onChange={setField('langfristigaSkulder5ar')}
                                    onBlur={scheduleSave}
                                />
                                <span>{' '}kr till betalning senare än fem år efter balansdagen.</span>
                            </Zone>
                        </div>
                        <p className="ard-hint">
                            Uppgiften är obligatorisk (ÅRL 5:13) — ange 0 om ingen del
                            förfaller senare än fem år.
                        </p>
                    </>
                )}

                {/* Obligatoriska ja/nej-frågor (Codex-granskning 6): ett
                    frivilligt fält skiljer inte "har inga" från "aldrig
                    besvarad" (ÅRL 5:14/5:15/5:19/5:22). */}
                {!readOnly && (
                    <>
                        <h3 className="ard-h2">{notTitle('stallda_sakerheter', 'Ställda säkerheter')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har företaget ställt säkerheter (panter, företagsinteckningar, äganderättsförbehåll m.m.)?"
                            value={form.stalldaSakerheterFinns}
                            onAnswer={answerBool('stalldaSakerheterFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:14) — nej är ett lika giltigt svar."
                        >
                            {form.stalldaSakerheterFinns === true && (
                                <>
                                    <div className="ard-p">
                                        <Zone {...zoneCtx} needsInput={!(parseOptionalAmount(form.stalldaSakerheter) > 0)}>
                                            <span>Ställda säkerheter:{' '}</span>
                                            <input
                                                className="ard-inline-input ard-num-input"
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0"
                                                aria-label="Ställda säkerheter (kr)"
                                                value={form.stalldaSakerheter}
                                                onChange={setField('stalldaSakerheter')}
                                                onBlur={scheduleSave}
                                            />
                                            <span>{' '}kr</span>
                                        </Zone>
                                    </div>
                                    <div className="ard-p">
                                        <Zone {...zoneCtx} needsInput={form.stalldaSakerheterText.trim() === ''}>
                                            <input
                                                className="ard-inline-input"
                                                type="text"
                                                style={{ width: '100%', maxWidth: '42ch' }}
                                                placeholder='Art och form, t.ex. "Företagsinteckningar" — ange särskilt säkerheter/åtaganden till förmån för koncernföretag (ÅRL 5:14, 5:16)'
                                                aria-label="Ställda säkerheter — art och form"
                                                value={form.stalldaSakerheterText}
                                                onChange={setField('stalldaSakerheterText')}
                                                onBlur={scheduleSave}
                                            />
                                        </Zone>
                                    </div>
                                </>
                            )}
                        </JaNejZone>

                        <h3 className="ard-h2">{notTitle('eventualforpliktelser', 'Eventualförpliktelser')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har företaget eventualförpliktelser (garantiåtaganden, borgensförbindelser eller liknande)?"
                            value={form.eventualforpliktelserFinns}
                            onAnswer={answerBool('eventualforpliktelserFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:15) — vid ja anges summan."
                        >
                            {form.eventualforpliktelserFinns === true && (
                                <>
                                    <div className="ard-p">
                                        <Zone {...zoneCtx} needsInput={!(parseOptionalAmount(form.eventualforpliktelser) > 0)}>
                                            <span>Eventualförpliktelser:{' '}</span>
                                            <input
                                                className="ard-inline-input ard-num-input"
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="0"
                                                aria-label="Eventualförpliktelser (kr)"
                                                value={form.eventualforpliktelser}
                                                onChange={setField('eventualforpliktelser')}
                                                onBlur={scheduleSave}
                                            />
                                            <span>{' '}kr</span>
                                        </Zone>
                                    </div>
                                    <div className="ard-p">
                                        <Zone {...zoneCtx} needsInput={false}>
                                            <input
                                                className="ard-inline-input"
                                                type="text"
                                                style={{ width: '100%', maxWidth: '42ch' }}
                                                placeholder='Frivillig beskrivning, t.ex. "Borgensåtagande för dotterbolag"'
                                                aria-label="Eventualförpliktelser — beskrivning"
                                                value={form.eventualforpliktelserText}
                                                onChange={setField('eventualforpliktelserText')}
                                                onBlur={scheduleSave}
                                            />
                                        </Zone>
                                    </div>
                                </>
                            )}
                        </JaNejZone>

                        <h3 className="ard-h2">{notTitle('exceptionella_poster', 'Exceptionella intäkter och kostnader')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har företaget intäkter eller kostnader som är exceptionella i fråga om storlek eller förekomst?"
                            value={form.exceptionellaPosterFinns}
                            onAnswer={answerBool('exceptionellaPosterFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:19) — vid ja anges arten och storleken av varje post."
                        >
                            {form.exceptionellaPosterFinns === true && (
                                <Zone {...zoneCtx} needsInput={form.exceptionellaPoster.trim() === ''}>
                                    <textarea
                                        className="ard-textarea"
                                        rows={2}
                                        placeholder="Arten och storleken av varje exceptionell post (ÅRL 5:19)."
                                        aria-label="Exceptionella intäkter och kostnader"
                                        value={form.exceptionellaPoster}
                                        onChange={setField('exceptionellaPoster')}
                                        onBlur={scheduleSave}
                                    />
                                </Zone>
                            )}
                        </JaNejZone>

                        <h3 className="ard-h2">{notTitle('handelser_efter_aret', 'Väsentliga händelser efter räkenskapsårets slut')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har väsentliga händelser inträffat efter räkenskapsårets slut?"
                            value={form.handelserEfterAretFinns}
                            onAnswer={answerBool('handelserEfterAretFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:22) — vid ja anges händelsernas art och deras ekonomiska effekt."
                        >
                            {form.handelserEfterAretFinns === true && (
                                <Zone {...zoneCtx} needsInput={form.handelserEfterAret.trim() === ''}>
                                    <textarea
                                        className="ard-textarea"
                                        rows={2}
                                        placeholder="Händelsernas art och deras ekonomiska effekt (ÅRL 5:22)."
                                        aria-label="Väsentliga händelser efter räkenskapsårets slut"
                                        value={form.handelserEfterAret}
                                        onChange={setField('handelserEfterAret')}
                                        onBlur={scheduleSave}
                                    />
                                </Zone>
                            )}
                        </JaNejZone>
                    </>
                )}

                {/* Lån till ledande befattningshavare (ÅRL 5:18) — frågan är
                    ALLTID obligatorisk; noten skrivs bara ut vid ja. */}
                {!readOnly && (
                    <>
                        <h3 className="ard-h2">{notTitle('lan_ledande', 'Lån till ledande befattningshavare')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har företaget lämnat lån eller ställt säkerheter till förmån för styrelseledamot eller VD?"
                            value={form.lanLedandeFinns}
                            onAnswer={answerBool('lanLedandeFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:18) — nej är ett lika giltigt svar; noten skrivs bara ut vid ja."
                        >
                            {form.lanLedandeFinns === true && (
                                <Zone {...zoneCtx} needsInput={form.lanLedandeText.trim() === ''}>
                                    <textarea
                                        className="ard-textarea"
                                        rows={2}
                                        placeholder="Ange belopp, villkor, ränta och under året återbetalda belopp (ÅRL 5:18)."
                                        aria-label="Lån till ledande befattningshavare"
                                        value={form.lanLedandeText}
                                        onChange={setField('lanLedandeText')}
                                        onBlur={scheduleSave}
                                    />
                                </Zone>
                            )}
                        </JaNejZone>
                    </>
                )}

                {/* Överföringar mellan AT-poster (ÅRL 5:8) — obligatorisk
                    ja/nej när anläggningsnoter finns: en omklassificering kan
                    inte särskiljas från inköp/avgång i huvudboken. */}
                {!readOnly && model?.noterModel?.conditions?.hasAnlaggningsnoter && (
                    <>
                        <h3 className="ard-h2">{notTitle('at_overforingar', 'Överföringar mellan poster av anläggningstillgångar')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har tillgångar omklassificerats (förts över) mellan anläggningsposter under året?"
                            value={form.atOverforingarFinns}
                            onAnswer={answerBool('atOverforingarFinns')}
                            hint="Obligatorisk uppgift (ÅRL 5:8 — överföringar är en egen kategori i notens rörelseschema); nej är ett lika giltigt svar."
                        >
                            {form.atOverforingarFinns === true && (
                                <Zone {...zoneCtx} needsInput={form.atOverforingarText.trim() === ''}>
                                    <textarea
                                        className="ard-textarea"
                                        rows={2}
                                        placeholder="Beskriv vilka poster som berörs och med vilka belopp (ÅRL 5:8)."
                                        aria-label="Överföringar mellan anläggningsposter"
                                        value={form.atOverforingarText}
                                        onChange={setField('atOverforingarText')}
                                        onBlur={scheduleSave}
                                    />
                                </Zone>
                            )}
                        </JaNejZone>
                    </>
                )}

                {/* Rättelse av fel (nya K2, BFNAR 2025:2 p. 2.12 tredje
                    meningen + 18.7A) — obligatorisk ja/nej ENDAST när servern
                    flaggar asksRattelseFel (rapporter på 2025-06-16-versionen,
                    FY som inleds efter 2025-12-31); upplysningstext krävs vid ja. */}
                {!readOnly && conditions.asksRattelseFel && (
                    <>
                        <h3 className="ard-h2">{notTitle('rattelse_fel', 'Rättelse av fel')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Har företaget under året rättat ett fel som avser ett tidigare räkenskapsår?"
                            value={form.rattelseFelFinns}
                            onAnswer={answerBool('rattelseFelFinns')}
                            hint="Obligatorisk uppgift (K2 punkt 2.12) — en rättelse som redovisats i resultat- eller balansräkningen kräver upplysning (punkt 18.7A); nej är ett lika giltigt svar."
                        >
                            {form.rattelseFelFinns === true && (
                                <Zone {...zoneCtx} needsInput={form.rattelseFelText.trim() === ''}>
                                    <textarea
                                        className="ard-textarea"
                                        rows={2}
                                        placeholder={'T.ex. "Företaget har rättat fel genom att redovisa effekterna av rättelsen i resultaträkningen för innevarande år." (K2 punkt 18.7A)'}
                                        aria-label="Rättelse av fel"
                                        value={form.rattelseFelText}
                                        onChange={setField('rattelseFelText')}
                                        onBlur={scheduleSave}
                                    />
                                </Zone>
                            )}
                        </JaNejZone>
                    </>
                )}

                {/* Moderföretag (ÅRL 5:21) — obligatorisk fråga; namn, org.nr
                    och säte krävs vid ja. */}
                {!readOnly && (
                    <>
                        <h3 className="ard-h2">{notTitle('moderforetag', 'Moderföretag')}</h3>
                        <JaNejZone
                            readOnly={readOnly}
                            question="Är företaget dotterföretag till ett moderföretag som upprättar koncernredovisning?"
                            value={form.moderAr}
                            onAnswer={answerBool('moderAr')}
                            hint="Obligatorisk uppgift (ÅRL 5:21) — noten skrivs bara ut vid ja."
                        >
                            {form.moderAr === true && (
                                <div className="ard-moder-fields">
                                    <Zone {...zoneCtx} needsInput={form.moderNamn.trim() === ''}>
                                        <input
                                            className="ard-inline-input"
                                            type="text"
                                            placeholder="Moderföretagets namn"
                                            aria-label="Moderföretagets namn"
                                            value={form.moderNamn}
                                            onChange={setField('moderNamn')}
                                            onBlur={scheduleSave}
                                        />
                                    </Zone>
                                    <Zone {...zoneCtx} needsInput={form.moderOrgnr.trim() === ''}>
                                        <input
                                            className="ard-inline-input"
                                            type="text"
                                            placeholder="Org.nr"
                                            aria-label="Moderföretagets organisationsnummer"
                                            value={form.moderOrgnr}
                                            onChange={setField('moderOrgnr')}
                                            onBlur={scheduleSave}
                                        />
                                    </Zone>
                                    <Zone {...zoneCtx} needsInput={form.moderSate.trim() === ''}>
                                        <input
                                            className="ard-inline-input"
                                            type="text"
                                            placeholder="Säte (kommun)"
                                            aria-label="Moderföretagets säte"
                                            value={form.moderSate}
                                            onChange={setField('moderSate')}
                                            onBlur={scheduleSave}
                                        />
                                    </Zone>
                                </div>
                            )}
                        </JaNejZone>
                    </>
                )}

                {filledOptional.map(([field, title, help, kind]) => (
                    <React.Fragment key={field}>
                        <h3 className="ard-h2">{optionalHeading(field, title)}</h3>
                        {renderOptionalBody(field, title, help, kind)}
                    </React.Fragment>
                ))}

                {!readOnly && emptyOptional.length > 0 && (
                    <div className="ard-optional">
                        {!showAllNotes ? (
                            <button
                                type="button"
                                className="btn btn-link btn-sm px-0"
                                onClick={() => setShowAllNotes(true)}
                            >
                                <Plus size={13} style={{ marginRight: 4 }} />
                                Fler noter vid behov ({emptyOptional.map(([, t]) => t.toLowerCase()).slice(0, 3).join(', ')}…)
                            </button>
                        ) : (
                            emptyOptional.map(([field, title, help, kind]) => (
                                <div key={field} className="ard-optional-note">
                                    <div className="ard-optional-title">{title} <span className="ard-unit">(skrivs bara ut om ifylld)</span></div>
                                    {renderOptionalBody(field, title, help, kind)}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Egna noter — renderas sist i dokumentet (notmodellen). */}
                {readOnly ? (
                    form.customNotes
                        .filter((c) => c.title.trim() !== '' && c.text.trim() !== '')
                        .map((c, i) => (
                            <React.Fragment key={i}>
                                <h3 className="ard-h2">{c.title}</h3>
                                <p className="ard-p">{c.text}</p>
                            </React.Fragment>
                        ))
                ) : (
                    <div className="ard-optional">
                        {form.customNotes.map((c, i) => (
                            <div key={i} className="ard-optional-note">
                                <div className="ard-custom-head">
                                    <input
                                        className="ard-inline-input ard-custom-title"
                                        type="text"
                                        placeholder="Notens rubrik"
                                        aria-label={`Egen not ${i + 1}, rubrik`}
                                        value={c.title}
                                        onChange={setCustomNote(i, 'title')}
                                        onBlur={scheduleSave}
                                    />
                                    <button
                                        type="button"
                                        className="ye-icon-btn"
                                        onClick={() => removeCustomNote(i)}
                                        aria-label="Ta bort noten"
                                        title="Ta bort noten"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <textarea
                                    className="ard-textarea"
                                    rows={2}
                                    placeholder="Notens text — skrivs bara ut när både rubrik och text är ifyllda."
                                    aria-label={`Egen not ${i + 1}, text`}
                                    value={c.text}
                                    onChange={setCustomNote(i, 'text')}
                                    onBlur={scheduleSave}
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={addCustomNote}
                        >
                            <Plus size={14} />
                            Lägg till egen not
                        </button>
                    </div>
                )}
            </section>
        );
    };

    const renderUnderskrifter = () => {
        const anyName = form.boardMembers.some((m) => m.name.trim() !== '');
        return (
            <section>
                <h2 className="ard-h1">Underskrifter</h2>
                <p className="ard-p">
                    {form.sate.trim() ? `${form.sate}, ` : ''}den dag som framgår av
                    våra elektroniska underskrifter — undertecknandet sker i nästa steg.
                </p>
                {/* Revisorsfrågan — obligatorisk ja/nej före färdigställande:
                    har bolaget revisor ska revisionsberättelsen läggas fram på
                    stämman (ABL 7:11) och inges med årsredovisningen (ÅRL 8:3).
                    Nej är lika giltigt — de flesta mindre AB har valt bort
                    revisor. Sparas som fb_sections.revisor. */}
                <JaNejZone
                    readOnly={readOnly}
                    question="Har bolaget registrerad revisor?"
                    value={form.harRevisor}
                    onAnswer={answerBool('harRevisor')}
                    hint="Har bolaget revisor ska revisionsberättelsen läggas fram på årsstämman och lämnas in tillsammans med årsredovisningen."
                >
                    {form.harRevisor === true && (
                        <div className="ard-moder-fields">
                            <input
                                className="ard-inline-input"
                                type="text"
                                placeholder="Revisorns namn (valfritt)"
                                aria-label="Revisorns namn"
                                value={form.revisorNamn}
                                onChange={setField('revisorNamn')}
                                onBlur={scheduleSave}
                            />
                            <input
                                className="ard-inline-input"
                                type="text"
                                placeholder="Revisionsbyrå (valfritt)"
                                aria-label="Revisionsbyrå"
                                value={form.revisorByra}
                                onChange={setField('revisorByra')}
                                onBlur={scheduleSave}
                            />
                        </div>
                    )}
                </JaNejZone>
                {/* ABL 25:13 (kapitalbrist): understiger justerat EK hälften av
                    aktiekapitalet ska styrelsen GENAST upprätta kontrollbalans-
                    räkning — annars personligt betalningsansvar (25:18).
                    Indikatorn räknas på servern; intyget krävs av finalize. */}
                {/* FAIL-CLOSED (Codex-granskning 6): en OBERÄKNELIG indikator
                    visas och kräver intyget — aldrig en tyst grön väg. */}
                {suggestions?.kontrollbalans?.unavailable && (
                    <div
                        style={{
                            border: '1px solid #d97706', borderRadius: 10, padding: '0.9rem 1rem',
                            margin: '0.75rem 0', background: 'rgba(217,119,6,0.06)', maxWidth: '78ch',
                        }}
                    >
                        <strong style={{ color: '#92400e' }}>Kapitalbristindikatorn kunde inte beräknas</strong>
                        <p className="ard-p" style={{ marginTop: 6 }}>
                            Kontrollera manuellt att justerat eget kapital inte understiger hälften av
                            det registrerade aktiekapitalet (ABL 25 kap. 13 § — annars ska styrelsen
                            genast upprätta en kontrollbalansräkning; personligt betalningsansvar
                            enligt 25 kap. 18 §), och bekräfta nedan.
                        </p>
                        {readOnly ? (
                            form.kontrollbalansAttest && (
                                <p className="ard-p" style={{ color: '#555' }}>
                                    Styrelsen har bekräftat att den är medveten om kontrollbalansskyldigheten.
                                </p>
                            )
                        ) : (
                            <Zone {...zoneCtx} needsInput={!form.kontrollbalansAttest}>
                                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={form.kontrollbalansAttest}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setForm((prev) => ({ ...prev, kontrollbalansAttest: checked }));
                                            scheduleSave();
                                        }}
                                    />
                                    <span>
                                        Styrelsen har kontrollerat kapitaltäckningen manuellt och är
                                        medveten om skyldigheten enligt ABL 25 kap. 13 §.
                                    </span>
                                </label>
                            </Zone>
                        )}
                    </div>
                )}
                {suggestions?.kontrollbalans?.triggered && (
                    <div
                        style={{
                            border: '1px solid #dc2626', borderRadius: 10, padding: '0.9rem 1rem',
                            margin: '0.75rem 0', background: 'rgba(220,38,38,0.06)', maxWidth: '78ch',
                        }}
                    >
                        <strong style={{ color: '#b91c1c' }}>Möjlig kapitalbrist — kontrollbalansräkning kan krävas</strong>
                        <p className="ard-p" style={{ marginTop: 6 }}>
                            Justerat eget kapital ({(suggestions.kontrollbalans.justeratEk ?? 0).toLocaleString('sv-SE')} kr,
                            inklusive eget kapital-delen av obeskattade reserver) understiger hälften av det
                            bokförda aktiekapitalet ({(suggestions.kontrollbalans.halftenAvAktiekapital ?? 0).toLocaleString('sv-SE')} kr
                            av {(suggestions.kontrollbalans.aktiekapital ?? 0).toLocaleString('sv-SE')} kr).
                            Då ska styrelsen <strong>genast</strong> upprätta en kontrollbalansräkning
                            (aktiebolagslagen 25 kap. 13 §) — annars riskerar ledamöterna personligt
                            betalningsansvar (25 kap. 18 §). Hantera frågan med revisor eller rådgivare;
                            det registrerade aktiekapitalet hos Bolagsverket är det rättsligt styrande.
                        </p>
                        {readOnly ? (
                            form.kontrollbalansAttest && (
                                <p className="ard-p" style={{ color: '#555' }}>
                                    Styrelsen har bekräftat att den är medveten om kontrollbalansskyldigheten.
                                </p>
                            )
                        ) : (
                            <Zone {...zoneCtx} needsInput={!form.kontrollbalansAttest}>
                                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={form.kontrollbalansAttest}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setForm((prev) => ({ ...prev, kontrollbalansAttest: checked }));
                                            scheduleSave();
                                        }}
                                    />
                                    <span>
                                        Styrelsen är medveten om skyldigheten enligt ABL 25 kap. 13 § och
                                        hanterar kontrollbalansfrågan.
                                    </span>
                                </label>
                            </Zone>
                        )}
                    </div>
                )}
                <Zone {...zoneCtx} needsInput={!anyName}>
                    <div className="ard-signatures">
                        {form.boardMembers.map((m, i) => (
                            <div className="ard-signature" key={i}>
                                <div className="ard-signature-line" />
                                {readOnly ? (
                                    <>
                                        <div className="ard-signature-name">{m.name || '—'}</div>
                                        <div className="ard-signature-role">{ROLE_LABEL[m.role] || m.role}</div>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            className="ard-inline-input ard-signature-input"
                                            type="text"
                                            placeholder="För- och efternamn"
                                            aria-label={`Undertecknare ${i + 1}, namn`}
                                            value={m.name}
                                            onChange={setBoardField(i, 'name')}
                                            onBlur={scheduleSave}
                                        />
                                        <div className="ard-signature-role-row">
                                            <select
                                                className="ard-inline-select"
                                                aria-label={`Undertecknare ${i + 1}, roll`}
                                                value={m.role}
                                                onChange={setBoardField(i, 'role')}
                                                onBlur={scheduleSave}
                                            >
                                                {BOARD_ROLES.map((r) => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                            {form.boardMembers.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="ye-icon-btn"
                                                    onClick={() => removeBoardMember(i)}
                                                    aria-label="Ta bort undertecknaren"
                                                    title="Ta bort undertecknaren"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </Zone>
                {!readOnly && (
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2 mt-2"
                        onClick={addBoardMember}
                    >
                        <Plus size={14} />
                        Lägg till undertecknare
                    </button>
                )}
                <p className="ard-hint">
                    Alla styrelseledamöter och eventuell VD ska skriva under
                    årsredovisningen (ÅRL 2:7).
                </p>
                {/* ÅRL 2:7-intyget: listan komplett (samtliga registrerade
                    ledamöter + ev. VD) — registeravstämning mot Bolagsverket
                    kräver API (Gate A); intyget ÄR kontrollen. Finalize
                    vägrar utan det. */}
                {readOnly ? (
                    form.styrelseKomplettAttest && (
                        <p className="ard-hint">
                            Styrelsen har bekräftat att undertecknarlistan är komplett.
                        </p>
                    )
                ) : (
                    <Zone {...zoneCtx} needsInput={!form.styrelseKomplettAttest}>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', maxWidth: '78ch' }}>
                            <input
                                type="checkbox"
                                checked={form.styrelseKomplettAttest}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setForm((prev) => ({ ...prev, styrelseKomplettAttest: checked }));
                                    scheduleSave();
                                }}
                            />
                            <span>
                                Listan omfattar bolagets samtliga registrerade styrelseledamöter
                                och, i förekommande fall, verkställande direktören. Har någon
                                anmält avvikande mening (antecknad i styrelseprotokollet) ska
                                yttrandet fogas till årsredovisningen (ÅRL 2:7).
                            </span>
                        </label>
                    </Zone>
                )}
            </section>
        );
    };

    const renderActions = () => (
        <div className="ard-actions">
            {finalizeError && (
                <div className="alert alert-danger mb-3" style={{ fontSize: '0.875rem', maxWidth: 640 }}>
                    <strong>{finalizeError}</strong>
                    {finalizeProblems && (
                        <ul className="mb-0 mt-2">
                            {finalizeProblems.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                    )}
                </div>
            )}
            <div className="ye-proposal-actions" style={{ marginTop: 0 }}>
                <button
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                    onClick={handleOpenPdf}
                    disabled={openingPdf}
                >
                    <FileText size={16} />
                    {openingPdf ? 'Öppnar…' : 'Förhandsgranska PDF'}
                </button>
                {!readOnly && (
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleFinalize}
                        disabled={finalizing || saveState === 'saving' || !dispositionOk}
                    >
                        <FileCheck2 size={16} />
                        {finalizing ? 'Färdigställer…' : 'Färdigställ för underskrift'}
                    </button>
                )}
                <span className="ye-footer-hint">
                    Siffrorna hämtas alltid direkt från bokföringen — de kan aldrig bli
                    inaktuella i dokumentet.
                </span>
            </div>
        </div>
    );

    const renderReadOnlyBanner = () => (
        <div className="ye-well" style={{ marginTop: 0, marginBottom: '1.25rem' }}>
            {isSignedOrLater ? (
                <>
                    Årsredovisningen är underskriven och låst för ändringar. Behöver du
                    ändra något? Ångra underskrifterna i steget Underskrifter &amp;
                    årsstämma först — sedan kan du redigera här igen.
                </>
            ) : (
                <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                    Årsredovisningen är färdigställd och klar för underskrift.
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => setEditing(true)}
                    >
                        <Pencil size={13} />
                        Ändra
                    </button>
                </span>
            )}
        </div>
    );

    const renderBody = () => {
        if (loadError) {
            return (
                <>
                    <div className="alert alert-danger" style={{ fontSize: '0.875rem' }}>
                        {loadError}
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchReport()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Försök igen
                    </button>
                </>
            );
        }
        if (!form || !model) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Hämtar årsredovisningen…
                </p>
            );
        }
        return (
            <>
                {readOnly && renderReadOnlyBanner()}
                {saveError && (
                    <div className="alert alert-danger" style={{ fontSize: '0.8125rem', maxWidth: 640 }}>
                        {saveError}
                    </div>
                )}
                {!readOnly && (
                    <p className="ard-legend">
                        Det här är din årsredovisning som den kommer att se ut. Fält med en
                        pulserande prick behöver fyllas i — allt annat hämtas ur bokföringen.
                    </p>
                )}
                <div className="ard-paper">
                    {renderFramsida()}
                    {renderForvaltningsberattelse()}
                    {renderResultatrakning()}
                    {renderBalansrakning()}
                    {renderNoter()}
                    {renderUnderskrifter()}
                </div>
                {renderActions()}
            </>
        );
    };

    // ---- Layout -----------------------------------------------------------

    return (
        <>
            <style>{`
                .ard-info-btn { background: none; border: none; padding: 0 2px; color: var(--bs-secondary-color, #6c757d); vertical-align: middle; cursor: help; }
                .ard-info-btn:hover, .ard-info-btn:focus-visible { color: var(--primary); outline: none; }
                .ard-legend { font-size: 0.85rem; color: #6b7280; max-width: 68ch; margin-bottom: 1rem; }

                .ard-paper {
                    font-family: Helvetica, Arial, sans-serif;
                    color: #111827;
                    line-height: 1.5;
                    padding-left: 1.75rem; /* rymd åt pulsprickarna i vänsterkanten */
                }
                @media (max-width: 640px) { .ard-paper { padding-left: 1.4rem; } }

                .ard-front { margin-bottom: 3rem; }
                .ard-title-page { text-align: center; padding: 2.5rem 0 3.5rem; }
                .ard-doc-type { font-size: 1.9rem; font-weight: 700; }
                .ard-for { font-size: 0.95rem; font-weight: 600; margin: 0.7rem 0; }
                .ard-company { font-size: 1.55rem; font-weight: 700; margin-bottom: 0.4rem; }
                .ard-meta { color: #374151; font-size: 0.98rem; }
                .ard-rakenskapsar { margin-top: 1.75rem; }

                .ard-h1 {
                    font-size: 1.35rem; font-weight: 700; margin: 4rem 0 1rem;
                }
                .ard-h2 { font-size: 1rem; font-weight: 700; margin: 1.6rem 0 0.5rem; }
                .ard-h3 { font-size: 0.92rem; font-weight: 700; margin: 1.1rem 0 0.35rem; }
                .ard-br-side { text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2.25rem; }
                .ard-unit { font-weight: 400; font-size: 0.8rem; color: #6b7280; }
                .ard-p { margin: 0 0 0.6rem; max-width: 65ch; white-space: pre-wrap; }
                .ard-hint { font-size: 0.8rem; color: #9ca3af; margin: 0.35rem 0 0; max-width: 60ch; }
                .ard-hint-inline { font-size: 0.78rem; color: #9ca3af; }
                .ard-sate-line { margin-top: 0.35rem; }

                /* Förslags-chips (registerdata) — att ta in bolagsordningens
                   text i ÅR är ett MEDVETET klick, aldrig ett tyst förval. */
                .ard-forslag { display: flex; flex-direction: column; gap: 0.4rem; margin: 0.35rem 0 0.6rem; max-width: 65ch; }
                .ard-forslag-chip {
                    text-align: left; font: inherit; font-size: 0.82rem; line-height: 1.45;
                    border: 1px dashed #d1d5db; border-radius: 8px; background: #f9fafb;
                    color: #374151; padding: 0.45rem 0.65rem; cursor: pointer;
                }
                .ard-forslag-chip:hover { border-color: var(--primary, #44814e); background: #f1f7f2; }
                .ard-forslag-use { color: var(--primary, #44814e); font-weight: 600; margin-right: 0.45rem; }
                .ard-forslag-src { display: block; font-size: 0.72rem; color: #9ca3af; margin-top: 0.15rem; }

                /* Redigerbara zoner — pulsen betyder "skriv här". */
                .ard-zone { position: relative; }
                .ard-dot {
                    position: absolute; left: -1.55rem; top: 0.45rem;
                    width: 11px; height: 11px; border-radius: 50%;
                    background: var(--primary, #44814e);
                    animation: ard-pulse 2s infinite;
                }
                @keyframes ard-pulse {
                    0% { box-shadow: 0 0 0 0 rgba(68, 129, 78, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(68, 129, 78, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(68, 129, 78, 0); }
                }
                @media (prefers-reduced-motion: reduce) { .ard-dot { animation: none; } }

                .ard-textarea {
                    display: block; width: 100%; max-width: 65ch;
                    font: inherit; color: inherit; line-height: inherit;
                    background: transparent; border: none; border-radius: 3px;
                    padding: 0.1rem 0.25rem; margin: 0 -0.25rem 0.6rem;
                    outline: 1px dashed #d1d5db; resize: vertical;
                    min-height: 2.6em; field-sizing: content;
                }
                .ard-textarea:hover { outline-color: #9ca3af; }
                .ard-textarea:focus { outline: 2px solid var(--primary, #44814e); background: #fbfdfb; }
                .ard-textarea::placeholder { color: #9ca3af; font-style: italic; }

                .ard-inline-input, .ard-inline-select {
                    font: inherit; color: inherit; background: transparent;
                    border: none; border-bottom: 1px dashed #9ca3af;
                    padding: 0 0.15rem; border-radius: 0;
                }
                .ard-inline-input:focus, .ard-inline-select:focus {
                    outline: none; border-bottom: 2px solid var(--primary, #44814e);
                    background: #fbfdfb;
                }
                .ard-inline-input::placeholder { color: #9ca3af; font-style: italic; }
                .ard-num-input { width: 9ch; text-align: right; font-variant-numeric: tabular-nums; }

                .ard-table-wrap { overflow-x: auto; margin-bottom: 1.25rem; }
                .ard-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
                .ard-table th {
                    text-align: right; font-weight: 700; font-size: 0.88rem; color: #111827;
                    padding: 0.3rem 0.4rem; border-bottom: 1px solid #9ca3af;
                }
                .ard-table th:first-child { text-align: left; }
                .ard-table td { padding: 0.26rem 0.4rem; vertical-align: top; }
                .ard-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
                .ard-indent { padding-left: 1.1rem !important; }
                .ard-group td { font-weight: 700; padding-top: 1rem; }
                .ard-subsum td { border-top: 1px solid #d1d5db; font-weight: 700; }
                .ard-sum td { border-top: 2px solid #374151; font-weight: 700; padding-top: 0.35rem; padding-bottom: 0.9rem; }

                .ard-signatures {
                    display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
                    gap: 2rem 2.5rem; margin-top: 2.25rem;
                }
                .ard-signature-line { border-bottom: 1px solid #374151; height: 2.2rem; margin-bottom: 0.35rem; }
                .ard-signature-name { font-weight: 600; }
                .ard-signature-role { font-size: 0.85rem; color: #6b7280; }
                .ard-signature-input { width: 100%; font-weight: 600; }
                .ard-signature-role-row { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.25rem; }
                .ard-inline-select { font-size: 0.85rem; color: #6b7280; }

                .ard-optional { margin-top: 1.5rem; border-top: 1px dashed #e5e7eb; padding-top: 0.75rem; }
                .ard-optional-note { margin-bottom: 0.9rem; }
                .ard-optional-title { font-weight: 600; font-size: 0.9rem; margin-bottom: 0.2rem; }

                /* Notkolumnen i RR/BR (ÅRL 5:3) — liten dämpad siffra före beloppen. */
                .ard-table th.ard-note-ref { text-align: center; font-weight: 400; color: #6b7280; font-size: 0.78rem; }
                .ard-table td.ard-note-ref { text-align: center; color: #9ca3af; font-size: 0.8rem; white-space: nowrap; width: 2.6rem; }

                /* Anläggningsnoternas beloppstabeller — smala, högerställda. */
                .ard-note-table { max-width: 36rem; }

                /* Ja/nej-frågor (ÅRL 5:18/5:21, revisorsfrågan). */
                .ard-janej { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; margin: 0.35rem 0; }
                .ard-janej-q { font-weight: 600; }
                .ard-janej-btns { display: inline-flex; gap: 0.35rem; }

                .ard-moder-fields { display: flex; gap: 1rem 1.5rem; flex-wrap: wrap; margin: 0.5rem 0 0.75rem; }
                .ard-moder-fields .ard-inline-input { min-width: 14ch; }

                .ard-custom-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.25rem; }
                .ard-custom-title { font-weight: 600; width: 100%; max-width: 40ch; }

                .ard-actions { margin-top: 1.5rem; }
                .ye-ar-savestate {
                    font-size: 0.75rem; color: #9ca3af;
                    display: inline-flex; align-items: center; gap: 0.3rem;
                }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Årsredovisning</h4>
                    <p className="section-subtitle">
                        Dokumentet nedan är din årsredovisning. Siffrorna byggs automatiskt
                        från bokföringen — fyll i fälten med pulserande prick, direkt i
                        dokumentet. Ändringarna sparas automatiskt.
                    </p>
                </div>
                <div className="action-buttons">
                    {saveState === 'saving' && (
                        <span className="ye-ar-savestate">
                            <RefreshCw size={12} className="spin" />
                            Sparar…
                        </span>
                    )}
                    {saveState === 'saved' && (
                        <span className="ye-ar-savestate">
                            <CheckCircle2 size={12} />
                            Sparat
                        </span>
                    )}
                    <span className={`ye-badge ${isDraft ? 'ye-badge-neutral' : 'ye-badge-success'}`}>
                        {REPORT_STATUS_LABELS[status] || status}
                    </span>
                </div>
            </div>

            <div className="modern-card-body">
                {renderBody()}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Årsredovisningen är klar
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {isDraft
                                ? 'Färdigställ årsredovisningen först — sedan kan steget markeras som klart.'
                                : 'Årsredovisningen är färdigställd — markera steget som klart och gå vidare till underskrifterna.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default AnnualReportStep;
